/**
 * API Client — shared between web and mobile
 *
 * REACT_APP_API_BASE_URL  → backend URL (set in consuming app's .env)
 * REACT_APP_USE_MOCK=true → use mock data (default: false = real API)
 *
 * ✅ MOBILE FIX: Now loads API_BASE_URL from app.json config for all API calls
 */

import axios from 'axios';

let API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

console.log("FINAL API URL:", API_BASE_URL);
let USE_MOCK     = process.env.REACT_APP_USE_MOCK === 'true';

// ─── Load mobile config if running in React Native ─────────────────────────────
try {
  const MobileConfig = require('../../mobile-app/src/config.js').default;
  if (MobileConfig && MobileConfig.API_BASE_URL && !API_BASE_URL) {
    API_BASE_URL = MobileConfig.API_BASE_URL;
    console.log('✅ Loaded mobile API URL:', API_BASE_URL);
  }
  if (MobileConfig && MobileConfig.USE_MOCK && !USE_MOCK) {
    USE_MOCK = MobileConfig.USE_MOCK;
    console.log('✅ Loaded mobile USE_MOCK:', USE_MOCK);
  }
} catch (e) {
  // Not in mobile context or config not found - using web config from process.env
}

// ─── Fallback to hardcoded IP if still empty ────────────────────────────────
if (!API_BASE_URL) {
  API_BASE_URL = 'https://app.crediflex.in';
  console.warn('⚠️ Using fallback API URL:', API_BASE_URL);
}

console.log(`🔧 API | mode: ${USE_MOCK ? '🟡 MOCK' : '🟢 REAL'} | url: ${API_BASE_URL}`);

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
  timeout: 30000,
});

// ─── Request interceptor — attach token ──────────────────────────────────────
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  console.log(`🌐 ${config.method?.toUpperCase()} ${config.url}`);
  return config;
});

// ─── Response interceptor — handle 401 ───────────────────────────────────────
apiClient.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      window.dispatchEvent(new Event('auth:logout'));
    }
    const data = error.response?.data;
    return Promise.reject(data || { message: error.message || 'Network error' });
  }
);

const delay   = (ms = 400) => new Promise(r => setTimeout(r, ms));
const mock    = async (data) => { await delay(); return { data }; };
const mockErr = async (msg, status = 400) => { await delay(); throw { status, message: msg }; };

// ─── AUTH ─────────────────────────────────────────────────────────────────────
export const login = async (credentials) => {
  if (USE_MOCK) {
    if (credentials.email === 'admin@crm.com' && credentials.password === 'admin123') {
      return mock({
        access_token:  'mock_token_' + Date.now(),
        refresh_token: 'mock_refresh_' + Date.now(),
        user: { id: 1, first_name: 'Admin', last_name: 'User', email: 'admin@crm.com', mobile_number: '9876543210', role: 1, designation: 'Manager' },
      });
    }
    return mockErr('Invalid email or password', 401);
  }
  return apiClient.post('/api/user/login/', credentials);
};

export const refreshToken = async (refresh) => {
  if (USE_MOCK) return mock({ access: 'mock_token_' + Date.now() });
  return apiClient.post('/api/token/refresh/', { refresh });
};

export const logout = async () => {
  if (USE_MOCK) return mock({ status: 'success' });
  return Promise.resolve({ data: { status: 'success' } });
};

export const forgotPassword = async (email) => {
  if (USE_MOCK) return mock({ message: 'OTP sent to email' });
  return apiClient.post('/api/v1/auth/forgot-password', { email });
};

export const verifyOTP = async (email, otp) => {
  if (USE_MOCK) {
    if (otp?.length === 6) return mock({ verified: true });
    return mockErr('Invalid OTP', 400);
  }
  return apiClient.post('/api/v1/auth/verify-otp', { email, otp });
};

export const resetPassword = async (temp_token, new_password) => {
  if (USE_MOCK) return mock({ message: 'Password reset successfully' });
  return apiClient.post('/api/v1/auth/reset-password', { temp_token, new_password });
};

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
export const getDashboardStats = async () => {
  if (USE_MOCK) return mock({ total_leads: 245, under_followup: 98, converted: 87, closed: 60, login_stage: 45, sanction_stage: 28, disbursed: 14, pdd_pending: 8, revenue_pending: 6 });
  return apiClient.get('/api/v1/dashboard/stats');
};

export const getFollowupsToday = async (date) => {
  if (USE_MOCK) return mock({ followups: [], total_count: 0, overdue_count: 0 });
  return apiClient.get('/api/v1/dashboard/followups-today', { params: { date } });
};

export const getExpectedDisbursements = async (date_from, date_to) => {
  if (USE_MOCK) return mock({ disbursements: [], total_count: 0, total_expected_amount: 0 });
  return apiClient.get('/api/v1/dashboard/expected-disb', { params: { date_from, date_to } });
};

export const getRecentActivity = async (limit = 10, offset = 0) => {
  if (USE_MOCK) return mock({ activities: [], total_count: 0 });
  return apiClient.get('/api/v1/dashboard/recent-activity', { params: { limit, offset } });
};

// ─── PRODUCTS ─────────────────────────────────────────────────────────────────
export const getProducts = async (params = {}) => {
  if (USE_MOCK) return mock({
    count: 14, next: null, previous: null,
    results: [
      { id:1,  name:'HL - Resale',       is_active:true },
      { id:2,  name:'HL - Construction', is_active:true },
      { id:3,  name:'HL - BT',           is_active:true },
      { id:4,  name:'Top Up',            is_active:true },
      { id:5,  name:'NRP',               is_active:true },
      { id:6,  name:'LAP',               is_active:true },
      { id:7,  name:'Equipment Loan',    is_active:true },
      { id:8,  name:'Personal Loan',     is_active:true },
      { id:9,  name:'Business Loan',     is_active:true },
      { id:10, name:'Education Loan',    is_active:true },
      { id:11, name:'Car Loan',          is_active:true },
      { id:12, name:'Gold Loan',         is_active:true },
      { id:13, name:'SME Loan',          is_active:true },
      { id:14, name:'CC / OD',           is_active:true },
    ],
  });
  return apiClient.get('/api/products/product/', { params });
};

export const getProductById = async (id) => {
  if (USE_MOCK) { const res = await getProducts(); return mock(res.data.results.find(p => p.id === parseInt(id)) || {}); }
  return apiClient.get(`/api/products/product/${id}/`);
};

export const createProduct = async (data) => {
  if (USE_MOCK) return mock({ id: Date.now(), ...data });
  return apiClient.post('/api/products/product/', data);
};

export const updateProduct = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.put(`/api/products/product/${id}/`, data);
};

export const deleteProduct = async (id) => {
  if (USE_MOCK) return mock({ message: 'Deleted' });
  return apiClient.delete(`/api/products/product/${id}/`);
};

// ─── INSTITUTIONS ─────────────────────────────────────────────────────────────
export const getInstitutions = async (params = {}) => {
  if (USE_MOCK) return mock({
    count: 3, next: null, previous: null,
    results: [
      { id:1, name:'HDFC Bank',     code:'HDFC',   institution_type:'BANK', is_active:true },
      { id:2, name:'Bajaj Finserv', code:'BAJAJ',  institution_type:'NBFC', is_active:true },
      { id:3, name:'LIC HFL',       code:'LICHFL', institution_type:'HFC',  is_active:true },
    ],
  });
  return apiClient.get('/api/leads/institution/', { params });
};

export const createInstitution = async (data) => {
  if (USE_MOCK) return mock({ id: Date.now(), ...data });
  return apiClient.post('/api/leads/institution/', data);
};

export const updateInstitution = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.put(`/api/leads/institution/${id}/`, data);
};

export const deleteInstitution = async (id) => {
  if (USE_MOCK) return mock({ message: 'Deleted' });
  return apiClient.delete(`/api/leads/institution/${id}/`);
};

export const importInstitutions = async (formData) => {
  if (USE_MOCK) return mock({ created_count: 0, skipped_count: 0, error_count: 0, message: 'Mock' });
  return apiClient.post('/api/leads/institution/import_excel/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// ─── LEAD STATUS ──────────────────────────────────────────────────────────────
export const getLeadStatuses = async (params = {}) => {
  if (USE_MOCK) return mock({
    count: 8, next: null, previous: null,
    results: [
      { id:1, name:'Follow Ups',   code:'follow_ups',   parent: null },
      { id:2, name:'Converted',    code:'converted',    parent: null },
      { id:3, name:'Closed',       code:'closed',       parent: null },
      { id:4, name:'Under Follow', code:'under_follow', parent:{ id:1, name:'Follow Ups', code:'follow_ups' } },
      { id:5, name:'Login',        code:'login',        parent:{ id:2, name:'Converted',  code:'converted'  } },
      { id:6, name:'Sanction',     code:'sanction',     parent:{ id:2, name:'Converted',  code:'converted'  } },
      { id:7, name:'Disbursement', code:'disbursement', parent:{ id:2, name:'Converted',  code:'converted'  } },
      { id:8, name:'PDD Cleared',  code:'pdd_cleared',  parent:{ id:2, name:'Converted',  code:'converted'  } },
    ],
  });
  return apiClient.get('/api/leads/status/', { params });
};

export const getLeadStatusParents = async (params = {}) => {
  if (USE_MOCK) return mock([
    { id:1, name:'Follow Ups', code:'follow_ups' },
    { id:2, name:'Converted',  code:'converted'  },
    { id:3, name:'Closed',     code:'closed'     },
  ]);
  return apiClient.get('/api/leads/status/only-parents/', { params });
};

export const getLeadStatusById = async (id) => {
  if (USE_MOCK) return mock({ id: parseInt(id), name:'Follow Ups', code:'follow_ups', parent: null });
  return apiClient.get(`/api/leads/status/${id}/`);
};

export const createLeadStatus = async (data) => {
  if (USE_MOCK) return mock({ id: Date.now(), ...data });
  return apiClient.post('/api/leads/status/', data);
};

export const updateLeadStatus = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.put(`/api/leads/status/${id}/`, data);
};

export const deleteLeadStatus = async (id) => {
  if (USE_MOCK) return mock({ message: 'Deleted' });
  return apiClient.delete(`/api/leads/status/${id}/`);
};

// ─── BSA ──────────────────────────────────────────────────────────────────────
export const getBsaList = async (params = {}) => {
  if (USE_MOCK) return mock({
    count: 3, next: null, previous: null,
    results: [
      { id:1, name:'Ramesh Patel', code:'CFBSA00001', mobile_number:'9876543210', email:'ramesh@example.com', pan:'ABCDE1234F', gst_number:'', constitution:'INDIVIDUAL',   bsa_type:'BROKER',    disbursement_payout_percentage:'1.50', bank_name:'HDFC Bank', account_number:'1234567890', ifsc_code:'HDFC0001234', account_holder_name:'Ramesh Patel', address:'Mumbai, MH', is_active:true,  documents:[] },
      { id:2, name:'Sunil Mehta',  code:'CFBSA00002', mobile_number:'9876543211', email:'sunil@example.com',  pan:'FGHIJ5678K', gst_number:'27ABCDE1234F1Z5', constitution:'PROPRIETORSHIP',  bsa_type:'CA',       disbursement_payout_percentage:'2.00', bank_name:'SBI',       account_number:'9876543210', ifsc_code:'SBIN0001234', account_holder_name:'Sunil Mehta',  address:'Pune, MH',   is_active:true,  documents:[] },
      { id:3, name:'Anita Sharma', code:'CFBSA00003', mobile_number:'9876543212', email:'anita@example.com',  pan:'',          gst_number:'', constitution:'INDIVIDUAL',   bsa_type:'AGENT',     disbursement_payout_percentage:'1.75', bank_name:'',          account_number:'',           ifsc_code:'',            account_holder_name:'',              address:'',            is_active:false, documents:[] },
    ],
  });
  return apiClient.get('/api/leads/bsa/', { params });
};

export const getBsaById = async (id) => {
  if (USE_MOCK) return mock({ id, name:'Ramesh Patel', code:'CFBSA00001', mobile_number:'9876543210', email:'ramesh@example.com', pan:'ABCDE1234F', gst_number:'', constitution:'INDIVIDUAL', bsa_type:'BROKER', disbursement_payout_percentage:'1.50', bank_name:'HDFC Bank', account_number:'1234567890', ifsc_code:'HDFC0001234', account_holder_name:'Ramesh Patel', address:'Mumbai, MH', is_active:true, documents:[] });
  return apiClient.get(`/api/leads/bsa/${id}/`);
};

export const createBsa = async (data) => {
  if (USE_MOCK) return mock({ id: Date.now(), code: 'CFBSA' + String(Date.now()).slice(-5), ...data });
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  return apiClient.post('/api/leads/bsa/', { ...data, created_by: user?.id || null, updated_by: user?.id || null });
};

export const updateBsa = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  return apiClient.put(`/api/leads/bsa/${id}/`, { ...data, updated_by: user?.id || null });
};

export const patchBsa = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  return apiClient.patch(`/api/leads/bsa/${id}/`, { ...data, updated_by: user?.id || null });
};

export const deleteBsa = async (id) => {
  if (USE_MOCK) return mock({ message: 'Deleted' });
  return apiClient.delete(`/api/leads/bsa/${id}/`);
};

// ─── BSA DOCUMENTS ────────────────────────────────────────────────────────────
export const getBsaDocuments = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/bsa-documents/', { params });
};

export const getBsaDocumentById = async (id) => {
  if (USE_MOCK) return mock({ id, document_type:'KYC_ID', file:'', bsa:0 });
  return apiClient.get(`/api/leads/bsa-documents/${id}/`);
};

export const createBsaDocument = async (formData) => {
  if (USE_MOCK) return mock({ id: Date.now(), document_type:'KYC_ID', file:'', bsa:0 });
  return apiClient.post('/api/leads/bsa-documents/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const updateBsaDocument = async (id, formData) => {
  if (USE_MOCK) return mock({ id });
  return apiClient.put(`/api/leads/bsa-documents/${id}/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const patchBsaDocument = async (id, data) => {
  if (USE_MOCK) return mock({ id });
  return apiClient.patch(`/api/leads/bsa-documents/${id}/`, data);
};

export const deleteBsaDocument = async (id) => {
  if (USE_MOCK) return mock({ message: 'Deleted' });
  return apiClient.delete(`/api/leads/bsa-documents/${id}/`);
};

// ─── CHANNELS ─────────────────────────────────────────────────────────────────
export const getChannels = async (params = {}) => {
  if (USE_MOCK) return mock({
    count: 4, next: null, previous: null,
    results: [
      { id:1, name:'Andro',       contact_person:'',   email:'', gstin:'', payout_percent:'0.00', billing_address:'' },
      { id:2, name:'RU Loans',    contact_person:'',   email:'', gstin:'', payout_percent:'0.00', billing_address:'' },
      { id:3, name:'RKPL',        contact_person:'',   email:'', gstin:'', payout_percent:'0.00', billing_address:'' },
      { id:4, name:'Urban Money', contact_person:'',   email:'', gstin:'', payout_percent:'0.00', billing_address:'' },
    ],
  });
  return apiClient.get('/api/leads/channel/', { params });
};

export const createChannel = async (data) => {
  if (USE_MOCK) return mock({ id: Date.now(), ...data });
  return apiClient.post('/api/leads/channel/', data);
};

export const updateChannel = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.put(`/api/leads/channel/${id}/`, data);
};

export const deleteChannel = async (id) => {
  if (USE_MOCK) return mock({ message: 'Deleted' });
  return apiClient.delete(`/api/leads/channel/${id}/`);
};

// ─── LEADS (main resource) ────────────────────────────────────────────────────
export const getLeads = async (params = {}) => {
  if (USE_MOCK) return mock({
    count: 3, next: null, previous: null,
    results: [
      { id:1, lead_no:'LD-2026-0001', customer_name:'Rajesh Kumar',  mobile_number:'9876543210', product:{id:1,name:'HL - Resale'}, institution:{id:1,name:'HDFC Bank'}, status:{id:1,name:'Follow Ups',code:'follow_ups',parent:null}, is_bsa:true, loan_amount:5000000, next_follow_up_date:'2026-03-01' },
      { id:2, lead_no:'LD-2026-0002', customer_name:'Priya Sharma',  mobile_number:'9876543211', product:{id:6,name:'LAP'}, institution:{id:2,name:'Bajaj Finserv'}, status:{id:5,name:'Login',code:'login',parent:{id:2,name:'Converted'}}, is_bsa:false, loan_amount:8000000, next_follow_up_date:'2026-03-05' },
      { id:3, lead_no:'LD-2026-0003', customer_name:'Amit Patel',    mobile_number:'9876543212', product:{id:2,name:'HL - Construction'}, institution:{id:3,name:'LIC HFL'}, status:{id:3,name:'Closed',code:'closed',parent:null}, is_bsa:false, loan_amount:7500000, next_follow_up_date:null },
    ],
  });
  return apiClient.get('/api/leads/leads/', { params });
};

export const getLeadById = async (id) => {
  if (USE_MOCK) { const res = await getLeads(); const lead = res.data.results.find(l => l.id === parseInt(id)); if (!lead) throw { status: 404, message: 'Lead not found' }; return mock(lead); }
  return apiClient.get(`/api/leads/leads/${id}/`);
};

export const createLead = async (data) => {
  if (USE_MOCK) return mock({ id: Date.now(), ...data });
  return apiClient.post('/api/leads/leads/', data);
};

export const updateLead = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.put(`/api/leads/leads/${id}/`, data);
};

export const patchLead = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.patch(`/api/leads/leads/${id}/`, data);
};

export const deleteLead = async (id) => {
  if (USE_MOCK) return mock({ message: 'Deleted' });
  return apiClient.delete(`/api/leads/leads/${id}/`);
};

export const closeLead = async (id, data) => {
  if (USE_MOCK) return mock({ id, status: 'closed', ...data });
  return apiClient.post(`/api/leads/leads/${id}/close/`, data);
};

export const completeLead = async (id) => {
  if (USE_MOCK) return mock({ id, status: 'completed' });
  return apiClient.post(`/api/leads/leads/${id}/complete/`);
};

// ─── LEAD LOGIN ───────────────────────────────────────────────────────────────
export const getLeadLogins = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/lead-login/', { params });
};

export const getLeadLoginById = async (id) => {
  if (USE_MOCK) return mock({ id });
  return apiClient.get(`/api/leads/lead-login/${id}/`);
};

export const getLeadLoginByLead = async (leadId) => {
  if (USE_MOCK) return mock(null);
  return apiClient.get('/api/leads/lead-login/', { params: { lead: leadId } });
};

export const createLeadLogin = async (data) => {
  if (USE_MOCK) return mock({ id: Date.now(), ...data });
  return apiClient.post('/api/leads/lead-login/', data);
};

export const updateLeadLogin = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.put(`/api/leads/lead-login/${id}/`, data);
};

export const patchLeadLogin = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.patch(`/api/leads/lead-login/${id}/`, data);
};

// ─── LEAD SANCTION ────────────────────────────────────────────────────────────
export const getLeadSanctions = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/lead-sanction/', { params });
};

export const getLeadSanctionById = async (id) => {
  if (USE_MOCK) return mock({ id });
  return apiClient.get(`/api/leads/lead-sanction/${id}/`);
};

export const getLeadSanctionByLead = async (leadId) => {
  if (USE_MOCK) return mock(null);
  return apiClient.get('/api/leads/lead-sanction/', { params: { lead: leadId } });
};

export const createLeadSanction = async (data) => {
  if (USE_MOCK) return mock({ id: Date.now(), ...data });
  if (data instanceof FormData) {
    return apiClient.post('/api/leads/lead-sanction/', data, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
  return apiClient.post('/api/leads/lead-sanction/', data);
};

export const updateLeadSanction = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  if (data instanceof FormData) {
    return apiClient.put(`/api/leads/lead-sanction/${id}/`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
  return apiClient.put(`/api/leads/lead-sanction/${id}/`, data);
};

export const patchLeadSanction = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  if (data instanceof FormData) {
    return apiClient.patch(`/api/leads/lead-sanction/${id}/`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
  return apiClient.patch(`/api/leads/lead-sanction/${id}/`, data);
};

// ─── LEAD DISBURSEMENT ────────────────────────────────────────────────────────
export const getLeadDisbursements = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/lead-disbursement/', { params });
};

export const getLeadDisbursementById = async (id) => {
  if (USE_MOCK) return mock({ id });
  return apiClient.get(`/api/leads/lead-disbursement/${id}/`);
};

export const getLeadDisbursementsPage = async (url) => {
  if (USE_MOCK) return mock({ count:0, results:[], next:null });
  return apiClient.get(url);
};

export const getLeadDisbursementsByLead = async (leadId) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/lead-disbursement/', { params: { lead: leadId } });
};

export const createLeadDisbursement = async (data) => {
  if (USE_MOCK) return mock({ id: Date.now(), ...data });
  return apiClient.post('/api/leads/lead-disbursement/', data);
};

export const updateLeadDisbursement = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.put(`/api/leads/lead-disbursement/${id}/`, data);
};

export const patchLeadDisbursement = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.patch(`/api/leads/lead-disbursement/${id}/`, data);
};

export const deleteLeadDisbursement = async (id) => {
  if (USE_MOCK) return mock({ message: 'Deleted' });
  return apiClient.delete(`/api/leads/lead-disbursement/${id}/`);
};

// ─── LEAD PDD ─────────────────────────────────────────────────────────────────
export const getLeadPdds = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/lead-pdd/', { params });
};

export const getLeadPddById = async (id) => {
  if (USE_MOCK) return mock({ id });
  return apiClient.get(`/api/leads/lead-pdd/${id}/`);
};

export const getLeadPddPage = async (url) => {
  if (USE_MOCK) return mock({ count:0, results:[], next:null });
  return apiClient.get(url);
};

export const getLeadPddByLead = async (leadId) => {
  if (USE_MOCK) return mock(null);
  return apiClient.get('/api/leads/lead-pdd/', { params: { lead: leadId } });
};

export const createLeadPdd = async (data) => {
  if (USE_MOCK) return mock({ id: Date.now(), ...data });
  return apiClient.post('/api/leads/lead-pdd/', data);
};

export const updateLeadPdd = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.put(`/api/leads/lead-pdd/${id}/`, data);
};

export const patchLeadPdd = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.patch(`/api/leads/lead-pdd/${id}/`, data);
};

// ─── BANKER ───────────────────────────────────────────────────────────────────
export const getBankers = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/banker/', { params });
};

export const getBankerByLead = async (leadId) => {
  if (USE_MOCK) return mock(null);
  return apiClient.get('/api/leads/banker/', { params: { lead: leadId } });
};

export const createBanker = async (data) => {
  if (USE_MOCK) return mock({ id: Date.now(), ...data });
  return apiClient.post('/api/leads/banker/', data);
};

export const updateBanker = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.put(`/api/leads/banker/${id}/`, data);
};

export const patchBanker = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.patch(`/api/leads/banker/${id}/`, data);
};

export const deleteBanker = async (id) => {
  if (USE_MOCK) return mock({ message: 'Deleted' });
  return apiClient.delete(`/api/leads/banker/${id}/`);
};

// ─── LEAD REVENUE ─────────────────────────────────────────────────────────────
export const getLeadRevenues = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/lead-revenue/', { params });
};

export const getLeadRevenueByLead = async (leadId) => {
  if (USE_MOCK) return mock(null);
  return apiClient.get('/api/leads/lead-revenue/', { params: { lead: leadId } });
};

export const createLeadRevenue = async (data) => {
  if (USE_MOCK) return mock({ id: Date.now(), ...data });
  return apiClient.post('/api/leads/lead-revenue/', data);
};

export const updateLeadRevenue = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.put(`/api/leads/lead-revenue/${id}/`, data);
};

export const patchLeadRevenue = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.patch(`/api/leads/lead-revenue/${id}/`, data);
};

// ─── INCENTIVE ────────────────────────────────────────────────────────────────
export const getIncentives = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/incentive/', { params });
};

export const calculateIncentive = async (data) => {
  if (USE_MOCK) return mock({ calculated: true, amount: 0 });
  return apiClient.post('/api/leads/incentive/calculate/', data);
};

export const lockIncentive = async (data) => {
  if (USE_MOCK) return mock({ locked: true });
  return apiClient.post('/api/leads/incentive/lock/', data);
};

export const getIncentiveBreakdown = async (params = {}) => {
  if (USE_MOCK) return mock({ count: 0, data: [] });
  return apiClient.get('/api/leads/incentive/breakdown/', { params });
};

// ─── MIS REPORTS ──────────────────────────────────────────────────────────────
export const getMisLastMonth = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/mis/last_month/', { params });
};

export const getMisOverall = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/mis/overall/', { params });
};

export const getMisPipeline = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/mis/pipeline/', { params });
};

export const getMisUserWise = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/mis/user_wise/', { params });
};

// NEW — Login MIS (filter by login_date action date)
export const getMisLoginMIS = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/mis/login_mis/', { params });
};

// NEW — Sanction MIS (filter by sanction_date action date)
export const getMisSanctionMIS = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/mis/sanction_mis/', { params });
};

// NEW — Disbursement MIS server-side (filter by disbursement_date action date)
export const getMisDisbursementMIS = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/mis/disbursement_mis/', { params });
};

// NEW — Channel Wise MIS
export const getMisChannelWise = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/mis/channel_wise/', { params });
};

// NEW — BSA Wise MIS
export const getMisBsaWise = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/mis/bsa_wise/', { params });
};

// NEW — Under Process MIS (FOLLOW_UP + LOGIN stage leads)
export const getMisUnderProcess = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/mis/under_process/', { params });
};

export const getMisChannelWiseLeads = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/mis/channel_wise_leads/', { params });
};

export const getMisBsaWiseLeads = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/mis/bsa_wise_leads/', { params });
};

// ─── INVOICE MANAGEMENT ─────────────────────────────────────────────────────────
export const getInvoicesPending = async (params = {}) => {
  if (USE_MOCK) return mock({ count:0, results:[] });
  return apiClient.get('/api/leads/leads/invoices_pending/', { params });
};

export const updateInvoice = async (data = {}) => {
  if (USE_MOCK) return mock({ success:true });
  return apiClient.post(`/api/leads/leads/${data.lead_id}/update_invoice/`, data);
};

// ─── USERS ────────────────────────────────────────────────────────────────────
export const getUsers = async (params = {}) => {
  if (USE_MOCK) return mock({
    count: 3, next: null, previous: null,
    results: [
      { id:1, first_name:'Admin', last_name:'User',  email:'admin@crm.com', mobile_number:'9876543210', role:1, designation:'Manager',        is_active:true  },
      { id:2, first_name:'John',  last_name:'Doe',   email:'john@crm.com',  mobile_number:'9876543211', role:2, designation:'Sales Manager',   is_active:true  },
      { id:3, first_name:'Jane',  last_name:'Smith', email:'jane@crm.com',  mobile_number:'9876543212', role:3, designation:'Sales Executive', is_active:false },
    ],
  });
  return apiClient.get('/api/user/users/', { params });
};

export const getUserById = async (id) => {
  if (USE_MOCK) return mock({ id:parseInt(id), first_name:'John', last_name:'Doe', email:'john@crm.com', mobile_number:'9876543211', role:2, designation:'Sales Manager', is_active:true });
  return apiClient.get(`/api/user/users/${id}/`);
};

export const createUser = async (data) => {
  if (USE_MOCK) return mock({ id: Date.now(), ...data });
  if (data instanceof FormData) {
    return apiClient.post('/api/user/users/', data, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
  return apiClient.post('/api/user/users/', data);
};

export const updateUser = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  if (data instanceof FormData) {
    return apiClient.put(`/api/user/users/${id}/`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
  return apiClient.put(`/api/user/users/${id}/`, data);
};

export const patchUser = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.patch(`/api/user/users/${id}/`, data);
};

export const deleteUser = async (id) => {
  if (USE_MOCK) return mock({ message: 'Deleted' });
  return apiClient.delete(`/api/user/users/${id}/`);
};

// ─── User Documents ─────────────────────────────────────────────────────────────
export const getUserDocuments = async (userId) => {
  if (USE_MOCK) return mock({ data: { results: [] } });
  return apiClient.get(`/api/user/documents/`, { params: { user: userId } });
};

export const createUserDocument = async (formData) => {
  if (USE_MOCK) return mock({ data: { id: Date.now() } });
  return apiClient.post('/api/user/documents/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const deleteUserDocument = async (id) => {
  if (USE_MOCK) return mock({ message: 'Deleted' });
  return apiClient.delete(`/api/user/documents/${id}/`);
};

// ─── RBAC ─────────────────────────────────────────────────────────────────────
export const getModules = async () => {
  if (USE_MOCK) return mock({ data: [
    { id:1, name:'User',   code:'user'   },
    { id:2, name:'Lead',   code:'lead'   },
    { id:3, name:'Report', code:'report' },
  ]});
  return apiClient.get('/api/rbac/module_list');
};

export const getPermissions = async () => {
  if (USE_MOCK) return mock({ data: [
    { id:1,  name:'create', code:'create', module:{id:1,name:'User'}   },
    { id:2,  name:'update', code:'update', module:{id:1,name:'User'}   },
    { id:3,  name:'read',   code:'read',   module:{id:1,name:'User'}   },
    { id:4,  name:'delete', code:'delete', module:{id:1,name:'User'}   },
    { id:5,  name:'create', code:'create', module:{id:2,name:'Lead'}   },
    { id:6,  name:'update', code:'update', module:{id:2,name:'Lead'}   },
    { id:7,  name:'read',   code:'read',   module:{id:2,name:'Lead'}   },
    { id:8,  name:'delete', code:'delete', module:{id:2,name:'Lead'}   },
    { id:9,  name:'create', code:'create', module:{id:3,name:'Report'} },
    { id:10, name:'read',   code:'read',   module:{id:3,name:'Report'} },
  ]});
  return apiClient.get('/api/rbac/permission_list');
};

export const getRoles = async () => {
  if (USE_MOCK) return mock({ data: [
    { id:1, name:'Administrator',   permissions:[{id:1},{id:2},{id:3},{id:4},{id:5},{id:6},{id:7},{id:8},{id:9},{id:10}] },
    { id:2, name:'Sales Manager',   permissions:[{id:1},{id:2},{id:3},{id:5},{id:6},{id:7}] },
    { id:3, name:'Sales Executive', permissions:[{id:1},{id:3},{id:5},{id:7}] },
  ]});
  return apiClient.get('/api/rbac/role/');
};

export const getRoleById = async (id) => {
  if (USE_MOCK) return mock({ id: parseInt(id), name:'Sample Role', permissions:[{id:1},{id:2},{id:3}] });
  return apiClient.get(`/api/rbac/role/${id}/`);
};

export const createRole = async (data) => {
  if (USE_MOCK) return mock({ id: Date.now(), ...data });
  return apiClient.post('/api/rbac/role/', data);
};

export const updateRole = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.put(`/api/rbac/role/${id}/`, data);
};

export const partialUpdateRole = async (id, data) => {
  if (USE_MOCK) return mock({ id, ...data });
  return apiClient.patch(`/api/rbac/role/${id}/`, data);
};

export const deleteRole = async (id) => {
  if (USE_MOCK) return mock({ message: 'Deleted' });
  return apiClient.delete(`/api/rbac/role/${id}/`);
};


// ─── DEFAULT EXPORT ───────────────────────────────────────────────────────────────────────────────────────
export default {
  // Auth
  login, refreshToken, logout, forgotPassword, verifyOTP, resetPassword,

  // Dashboard
  getDashboardStats, getFollowupsToday, getExpectedDisbursements, getRecentActivity,

  // Masters
  getProducts, getProductById, createProduct, updateProduct, deleteProduct,
  getInstitutions, createInstitution, updateInstitution, deleteInstitution, importInstitutions,
  getChannels, createChannel, updateChannel, deleteChannel,

  // Lead Status
  getLeadStatuses, getLeadStatusParents, getLeadStatusById,
  createLeadStatus, updateLeadStatus, deleteLeadStatus,

  // BSA
  getBsaList, getBsaById, createBsa, updateBsa, patchBsa, deleteBsa,
  getBsaDocuments, getBsaDocumentById, createBsaDocument, updateBsaDocument, patchBsaDocument, deleteBsaDocument,

  // Leads — main resource
  getLeads, getLeadById, createLead, updateLead, patchLead, deleteLead, closeLead, completeLead,

  // Lead workflow — stage resources
  getLeadLogins, getLeadLoginById, getLeadLoginByLead, createLeadLogin, updateLeadLogin, patchLeadLogin,
  getLeadSanctions, getLeadSanctionById, getLeadSanctionByLead, createLeadSanction, updateLeadSanction, patchLeadSanction,
  getLeadDisbursements, getLeadDisbursementById, getLeadDisbursementsPage, getLeadDisbursementsByLead,
  createLeadDisbursement, updateLeadDisbursement, patchLeadDisbursement, deleteLeadDisbursement,
  getLeadPddById, getLeadPddPage, getLeadPddByLead, createLeadPdd, updateLeadPdd, patchLeadPdd,
  getBankers, getBankerByLead, createBanker, updateBanker, patchBanker, deleteBanker,
  getLeadRevenues, getLeadRevenueByLead, createLeadRevenue, updateLeadRevenue, patchLeadRevenue,

  // MIS
  getMisLastMonth, getMisOverall, getMisPipeline, getMisUserWise,
  getMisLoginMIS, getMisSanctionMIS, getMisDisbursementMIS,
  getMisChannelWise, getMisBsaWise, getMisUnderProcess,
  getMisChannelWiseLeads, getMisBsaWiseLeads,

  // Incentive
  getIncentives, calculateIncentive, lockIncentive, getIncentiveBreakdown,

  // Invoice Management
  getInvoicesPending, updateInvoice,

  // Users & RBAC
  getUsers, getUserById, createUser, updateUser, patchUser, deleteUser,
  getUserDocuments, createUserDocument, deleteUserDocument,
  getModules, getPermissions,
  getRoles, getRoleById, createRole, updateRole, partialUpdateRole, deleteRole,
};